import { useEffect, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import { Box } from '@mui/material';
import _ from 'lodash';

// Local imports
import { App } from 'BMapsSrc/BMapsApp';
import { DialogPrimaryButton } from 'BMapsSrc/ui/common/DialogActionButtons';
import { createJob } from 'BMapsSrc/redux/jobs/thunks';
import { useDispatch } from 'BMapsSrc/redux';
import { jobDefinitions, JobTypes } from 'BMapsSrc/jobs/JobDefinition';
import { UserActions } from 'BMapsSrc/cmds';
import { TextArea } from 'BMapsSrc/ui/common/TextFields';
import Select from 'BMapsSrc/ui/common/Select';
import DialogContentSection from 'BMapsSrc/ui/common/DialogContentSection';
import { wrapForDisabledTooltip } from 'BMapsSrc/ui/common/helpers';
import BMapsTooltip from 'BMapsSrc/ui/BMapsTooltip';
import LinkLikeButton from 'BMapsSrc/ui/common/LinkLikeButton';
import {
    ImportErrors, PlaceHolder,
} from '../../import_components';

export default function CompoundGenerationTab({ initData }) {
    const dispatch = useDispatch();
    const [
        moleculeGenerationPrograms, setMoleculeGenerationPrograms,
    ] = useState([JobTypes.REINVENT]);
    const [chosenProgram, setChosenProgram] = useState(JobTypes.REINVENT);
    const [compounds, setCompounds] = useState(App.Workspace.getLoadedCompounds());
    const [errors, setErrors] = useState([]);
    const [molDataText, setMolDataText] = useState(
        initData?.selectedCompounds?.map((x) => x.getUnnamedSmiles()).join('\n')
        || initData?.molDataText
        || ''
    );

    async function generateCompounds(compoundSmiles) {
        const smiles = Array.isArray(compoundSmiles) ? compoundSmiles : [compoundSmiles];
        const { results, error } = await dispatch(
            createJob(JobTypes.REINVENT, { smiles })
        );
        if (results) {
            UserActions.OpenImport('jobs_compounds_tab', { jobid: results?.jobid });
            return true;
        } else {
            setErrors([`The job submission failed: ${error || 'Unspecified error'}`]);
            return false;
        }
    }
    const handleMenuItemSelect = (compound) => {
        const newInput = `${compound.getUnnamedSmiles()}\t${compound.displayName}`;
        if (!molDataText || molDataText === '') {
            setMolDataText(newInput);
            return;
        }
        setMolDataText(`${molDataText}\n${newInput}`);
    };
    // Update the Submit button (disabled / tooltip) as necessary
    let submitBtnTooltip = 'Click to submit compound generation job';
    let disabled = false;

    if (!molDataText || molDataText === '') {
        submitBtnTooltip = 'Include compound generation inputs above before submission';
        disabled = true;
    }

    useEffect(() => {
        setCompounds(App.Workspace.getLoadedCompounds().filter(
            (compound) => !molDataText.includes(compound.getUnnamedSmiles())
        ));
    }, [molDataText]);

    return (
        <div>
            <DialogContentSection title="Compound Generation">
                Generate compounds from smiles strings
                with an AI compound generation program.
            </DialogContentSection>
            <DialogContentSection title="1. Specify Compound Input" collapsible defaultExpand disablePaddingTop>
                {compounds.length > 0 && (
                    <Select
                        label="Add Workspace Compounds"
                        SelectProps={{
                            native: false,
                        }}
                        sx={{ width: '50%', margin: '1rem 0' }}
                    >
                        {compounds.map((compound) => (
                            <MenuItem
                                key={compound.smiles}
                                onClick={() => {
                                    handleMenuItemSelect(compound);
                                }}
                            >
                                {compound.displayName}
                            </MenuItem>
                        ))}
                    </Select>
                )}
                <CompoundInputBox
                    value={molDataText}
                    setValues={(e) => setMolDataText(e)}
                />
                <Select
                    label="Molecule Generation Program"
                    SelectProps={{
                        native: false,
                    }}
                    value={chosenProgram}
                    onChange={(e) => {
                        setChosenProgram(e.target.value);
                    }}
                    sx={{ width: '50%', marginTop: '1.5rem' }}
                >
                    {moleculeGenerationPrograms.map((program) => (
                        <MenuItem key={program} value={program}>
                            {jobDefinitions[program].getJobTypeName()}
                        </MenuItem>
                    ))}
                </Select>
            </DialogContentSection>
            <DialogContentSection title="2. Submit Compound Generation Job">
                <Box>
                    <BMapsTooltip title={submitBtnTooltip}>
                        {wrapForDisabledTooltip(
                            <DialogPrimaryButton
                                onClick={() => {
                                    const smiles = molDataText
                                        .split('\n')
                                        .map((s) => s.trim())
                                        .filter((s) => s !== '');
                                    generateCompounds(smiles);
                                }}
                                style={{ width: '100%' }}
                                disabled={disabled}
                            >
                                Submit for Compound Generation
                            </DialogPrimaryButton>
                        )}
                    </BMapsTooltip>
                    <span style={{ fontStyle: 'italic' }}>
                        Compound generation operations run as jobs outside your BMaps session.
                        <br />
                        The resulting structures will be will be available in the
                        {' '}
                        <LinkLikeButton
                            onClick={() => UserActions.OpenImport('jobs_compounds_tab')}
                                            // Unsetting vertical-align allows the button text to
                                            // line up nicely with the rest of the test.
                            style={{ padding: 0, verticalAlign: 'unset' }}
                        >
                            Compound Job Results
                        </LinkLikeButton>
                        {' '}
                        tab.
                    </span>

                </Box>
            </DialogContentSection>
            {!!errors && errors.length > 0 && <ImportErrors errors={errors} /> }
        </div>
    );
}

function CompoundInputBox({
    value, setValues,
}) {
    return (
        <Box sx={{ marginTop: '1rem' }}>
            <PlaceHolder $show={value === ''}>
                <>
                    Enter ligand smiles, one per line,
                    <br />
                    or select a compound from the workspace.
                </>
            </PlaceHolder>
            <TextArea
                autoFocus
                multiline
                minRows={1}
                value={value}
                onChange={(e) => setValues(e.target.value)}
                sx={{
                    '& textarea': {
                        resize: 'vertical',
                        minHeight: '6rem',
                    },
                }}
            />
        </Box>
    );
}
